import { differenceInMinutes, isSameDay } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { Absence } from 'RestAPIs/roster/types';

import { formatISOString, fromISOString } from 'utils/date';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

import { Icon } from '@sdb/planning-ui';

type Props = {
  absence: Absence;
  showTypeLabel?: boolean;
  showDate?: boolean;
};

export default function AbsenceTooltipContent({
  absence,
  showTypeLabel,
  showDate
}: Props) {
  const { t } = useTranslation('common');
  const startDate = fromISOString(absence.start);
  const endDate = fromISOString(absence.end);
  const isTheSameDay = isSameDay(startDate, endDate);
  const isOneFullDay =
    isTheSameDay && differenceInMinutes(endDate, startDate) >= 1439; // 23h & 59m
  const isLeave = absence.type === 'LEAVE';
  const isIllness = absence.type === 'ILLNESS';
  const isExternalIllness = isIllness && absence.isExternal;
  const DHTDescription = absence.dutyHourType?.description;
  const hasNote = isLeave && absence.note;
  const hasContent =
    showTypeLabel ||
    isLeave ||
    isExternalIllness ||
    showDate ||
    !isOneFullDay ||
    DHTDescription ||
    hasNote;

  if (!hasContent) return '';

  return (
    <Table size="small" sx={{ 'tr:last-child td': { border: 0 } }}>
      <TableBody>
        {showTypeLabel && (
          <TableRow>
            <TableCell>
              <strong>{t('type')}</strong>
            </TableCell>
            <TableCell>
              {t(isLeave ? 'leave' : isIllness ? 'illness' : 'absent')}
            </TableCell>
          </TableRow>
        )}
        {!isIllness && (
          <TableRow>
            <TableCell>
              <strong>{t('status')}</strong>
            </TableCell>
            <TableCell>
              {t(absence.isApproved ? 'accepted' : 'pending')}
            </TableCell>
          </TableRow>
        )}
        {isExternalIllness && (
          <TableRow>
            <TableCell colSpan={2}>
              <strong>{t('externalAbsence')}</strong>
            </TableCell>
          </TableRow>
        )}
        {(showDate || !isOneFullDay) && (
          <>
            <TableRow>
              <TableCell>
                <strong>{t(isTheSameDay ? 'startTime' : 'startDate')}</strong>
              </TableCell>
              <TableCell>
                {formatISOString(
                  absence.start,
                  isTheSameDay ? 'p' : 'dd-MM-yyy p'
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>{t(isTheSameDay ? 'endTime' : 'endDate')}</strong>
              </TableCell>
              <TableCell>
                {formatISOString(
                  absence.end,
                  isTheSameDay ? 'p' : 'dd-MM-yyy p'
                )}
              </TableCell>
            </TableRow>
          </>
        )}
        {DHTDescription && (
          <TableRow>
            <TableCell>
              <strong>{t('dutyHourType')}</strong>
            </TableCell>
            <TableCell>{DHTDescription}</TableCell>
          </TableRow>
        )}
        {hasNote && (
          <TableRow>
            <TableCell style={{ fontSize: 0 }}>
              <Icon type="comment" fontSize="small" />
            </TableCell>
            <TableCell>
              <div>
                <strong>{t('notes')}</strong>
              </div>
              <div>{absence.note}</div>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
